import React, { useState, useEffect, useRef } from "react";
import imageCompression from "browser-image-compression";
import { db } from "@/api/db";
import { toast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor, RichTextViewer } from "@/components/ui/rich-text-editor";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  User, Globe, Heart, HeartHandshake, Brain, FileText, Briefcase,
  Pencil, Loader2, Plus, Trash2, X, Mail, Instagram, Phone,
  MapPin, Check, ChevronsUpDown,
} from "lucide-react";
import ProfileOnboarding from "@/components/profile/ProfileOnboarding";
import {
  GENDER_OPTIONS,
  CIVIL_STATE_OPTIONS,
  CHILDREN_OPTIONS,
  IA_PROFICIENCY_OPTIONS,
  SENIORITY_OPTIONS,
  STATES,
  PURPOSE_QUESTIONS_ORDER,
  PURPOSE_QUESTION_LABELS,
} from "@/components/profile/constants";

// ── Helpers ──

function getInitials(name) {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function displayValue(val, fallback = "—") {
  if (val === null || val === undefined || val === "") return fallback;
  return val;
}

function labelFromValue(value, options) {
  const opt = options.find(o => o.value === value || o.value === String(value));
  return opt?.label || value || "—";
}

function lookupName(id, list) {
  if (!id || !list) return "—";
  const item = list.find(i => i.id === id);
  return item?.name || "—";
}

function formatCityName(city) {
  if (!city) return "";
  const lowerCaseWords = ["de", "da", "das", "do", "dos", "e"];
  return city.toLowerCase().split(" ").map((word, i) =>
    lowerCaseWords.includes(word) && i !== 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
  ).join(" ");
}

// ── Multi-select Searchable Component ──

function MultiSelectSearchable({ options, selected, onChange, placeholder, allowCustom }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedIds = new Set(selected.map(s => s.id).filter(Boolean));
  const selectedNames = new Set(selected.map(s => s.name?.toLowerCase()).filter(Boolean));
  const filtered = (options || []).filter(
    o => !selectedIds.has(o.id) && !selectedNames.has(o.name?.toLowerCase())
  );

  const addItem = (item) => {
    onChange([...selected, item]);
    setSearch("");
  };

  const removeItem = (index) => {
    onChange(selected.filter((_, i) => i !== index));
  };

  const addCustom = () => {
    const trimmed = search.trim();
    if (!trimmed) return;
    if (selectedNames.has(trimmed.toLowerCase())) return;
    onChange([...selected, { name: trimmed, custom: true }]);
    setSearch("");
  };

  const hasExactMatch = filtered.some(o => o.name?.toLowerCase() === search.trim().toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex w-full min-h-[40px] flex-wrap items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          {selected.length === 0 && (
            <span className="text-muted-foreground">{placeholder || "Adicionar..."}</span>
          )}
          {selected.map((item, i) => (
            <Badge key={i} variant="secondary" className="gap-1">
              {item.name}
              <X
                className="w-3 h-3 cursor-pointer hover:text-destructive"
                onClick={(e) => { e.stopPropagation(); removeItem(i); }}
              />
            </Badge>
          ))}
          <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {allowCustom && search.trim() ? (
                <button
                  type="button"
                  className="w-full px-2 py-1.5 text-sm text-left hover:bg-accent rounded cursor-pointer"
                  onClick={addCustom}
                >
                  <Plus className="w-3 h-3 inline mr-1" />
                  Criar "{search.trim()}"
                </button>
              ) : (
                "Nenhum resultado."
              )}
            </CommandEmpty>
            <CommandGroup>
              {filtered
                .filter(o => !search || o.name?.toLowerCase().includes(search.toLowerCase()))
                .map(opt => (
                  <CommandItem
                    key={opt.id}
                    value={opt.name}
                    onSelect={() => addItem({ id: opt.id, name: opt.name })}
                  >
                    {opt.name}
                  </CommandItem>
                ))}
              {allowCustom && search.trim() && !hasExactMatch && filtered.filter(o => o.name?.toLowerCase().includes(search.toLowerCase())).length > 0 && (
                <CommandItem onSelect={addCustom}>
                  <Plus className="w-3 h-3 mr-1" />
                  Criar "{search.trim()}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── Region Adder (state + searchable city selector) ──

let citiesJsonCache = null;

function RegionAdder({ onAdd }) {
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedCityCode, setSelectedCityCode] = useState("");
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const [cityPopoverOpen, setCityPopoverOpen] = useState(false);

  useEffect(() => {
    if (!selectedState) {
      setCities([]);
      setSelectedCity("");
      setSelectedCityCode("");
      return;
    }
    setSelectedCity("");
    setSelectedCityCode("");
    setCitySearch("");

    if (citiesJsonCache) {
      setCities(citiesJsonCache[selectedState] || []);
      return;
    }
    setLoadingCities(true);
    fetch('/cities.json')
      .then(r => r.json())
      .then(data => {
        citiesJsonCache = data;
        setCities(data[selectedState] || []);
      })
      .catch(() => setCities([]))
      .finally(() => setLoadingCities(false));
  }, [selectedState]);

  const handleAdd = () => {
    if (!selectedState) return;
    onAdd({
      state: selectedState,
      city: selectedCity || '',
      cityCode: selectedCityCode || '',
    });
    setSelectedState("");
    setSelectedCity("");
    setSelectedCityCode("");
    setCitySearch("");
  };

  const filteredCities = citySearch
    ? cities.filter(c => c.nome.toLowerCase().includes(citySearch.toLowerCase())).slice(0, 50)
    : cities.slice(0, 50);

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
      <div className="flex-1 space-y-1">
        <Label className="text-xs">Estado</Label>
        <Select value={selectedState} onValueChange={v => setSelectedState(v)}>
          <SelectTrigger>
            <SelectValue placeholder="Selecione o estado" />
          </SelectTrigger>
          <SelectContent>
            {STATES.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1 space-y-1">
        <Label className="text-xs">Cidade</Label>
        <Popover open={cityPopoverOpen} onOpenChange={setCityPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={!selectedState || loadingCities}
              className="flex w-full min-h-[40px] items-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className={selectedCity ? "text-foreground" : "text-muted-foreground"}>
                {loadingCities ? "Carregando..." : selectedCity ? formatCityName(selectedCity) : "Buscar cidade..."}
              </span>
              <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Buscar cidade..."
                value={citySearch}
                onValueChange={setCitySearch}
              />
              <CommandList>
                <CommandEmpty>Nenhuma cidade encontrada.</CommandEmpty>
                <CommandGroup>
                  {filteredCities.map(c => (
                    <CommandItem
                      key={c.codigo_ibge}
                      value={c.nome}
                      onSelect={() => {
                        setSelectedCity(c.nome);
                        setSelectedCityCode(c.codigo_ibge);
                        setCityPopoverOpen(false);
                        setCitySearch("");
                      }}
                    >
                      <Check className={`mr-2 h-4 w-4 ${selectedCity === c.nome ? "opacity-100" : "opacity-0"}`} />
                      {formatCityName(c.nome)}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAdd}
        disabled={!selectedState}
        className="shrink-0"
      >
        <Plus className="w-4 h-4 mr-1" /> Adicionar região
      </Button>
    </div>
  );
}

// ── Section Header ──

function SectionHeader({ icon: Icon, title, section, editingSection, onEdit, onCancel, onSave, saving }) {
  const isEditing = editingSection === section;

  return (
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
      <div className="flex items-center gap-2">
        <Icon className="w-5 h-5 text-muted-foreground" />
        <CardTitle className="text-lg">{title}</CardTitle>
      </div>
      {!isEditing ? (
        <Button variant="ghost" size="sm" onClick={() => onEdit(section)}>
          <Pencil className="w-4 h-4" />
        </Button>
      ) : (
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={() => onSave(section)}
            disabled={saving}
            style={{ background: "#00DCA4", color: "#003326" }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
          </Button>
        </div>
      )}
    </CardHeader>
  );
}

// ══════════════════════════════════════════════════════════
// ══  MAIN COMPONENT  ═════════════════════════════════════
// ══════════════════════════════════════════════════════════

export default function ProfileSettings() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [formData, setFormData] = useState({});
  const savingRef = useRef(false);
  const avatarInputRef = useRef(null);

  // ── Load profile ──
  const loadProfile = async () => {
    try {
      const res = await db.profile.get();
      setProfile(res);
    } catch {
      toast({ title: "Erro ao carregar perfil", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfile(); }, []);

  // ── Form helpers ──
  const updateForm = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const updateFormNested = (parent, key, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...(prev[parent] || {}), [key]: value },
    }));
  };

  // ── Start editing a section ──
  const startEdit = (section) => {
    const p = profile;
    const k = p.knowledge || {};

    const sectionDefaults = {
      social: {
        instagram: p.instagram || "",
        website: p.website || "",
      },
      personal: {
        fullName: p.fullName || "",
        gender: p.gender || "",
        nationalityId: p.nationalityId || "",
        children: p.children != null ? String(p.children) : "",
        civilState: p.civilState || "",
        birthDate: p.birthDate ? p.birthDate.substring(0, 10) : "",
        whatsapp: p.whatsapp || "",
        workEquipments: p.workEquipments || [],
        workEquipmentInput: "",
        address: {
          zipCode: p.addresses?.zipCode || "",
          street: p.addresses?.street || "",
          streetNumber: p.addresses?.streetNumber || "",
          complement: p.addresses?.complement || "",
          neighborhood: p.addresses?.neighborhood || "",
          city: p.addresses?.city || "",
          state: p.addresses?.state || "",
        },
      },
      preferences: {
        minSalary: p.minSalary ?? "",
        maxSalary: p.maxSalary ?? "",
        preferredPositions: (p.preferredPositions || []).map(pp => ({ ...pp })),
        contractModelIds: (p.contractModels || []).map(c => c.id),
        availabilityIds: (p.availability || []).map(a => a.id),
        jobModelIds: (p.jobModels || []).map(j => j.id),
        preferredRegions: (p.preferredRegions || []).map(r => ({
          state: r.state,
          city: r.city || '',
          cityCode: r.cityCode || '',
        })),
      },
      purpose: {
        questions: (p.purposeQuestions || []).map(q => ({ ...q })),
      },
      knowledge: {
        languages: (k.languages || []).map(l => ({ ...l })),
        skills: [
          ...(k.skills || []).map(s => ({ id: s.id, name: s.name })),
          ...(k.customSkills || []).map(s => ({ name: s.name || s, custom: true })),
        ],
        tools: [
          ...(k.tools || []).map(t => ({ id: t.id, name: t.name })),
          ...(k.customTools || []).map(t => ({ name: t.name || t, custom: true })),
        ],
        iaProficiency: k.iaProficiency || p.iaProficiency || "",
      },
      pitch: {
        professionalPitch: p.professionalPitch || "",
      },
      history: {
        entries: (p.professionalHistory || []).map(h => ({
          id: h.id || null,
          companyName: h.companyName || "",
          jobPositionId: h.jobPositionId || "",
          jobPosition: h.jobPosition || "",
          startDate: h.startDate ? h.startDate.substring(0, 10) : "",
          endDate: h.endDate ? h.endDate.substring(0, 10) : "",
          description: h.description || "",
          currentJob: h.currentJob || false,
        })),
      },
    };

    setFormData(sectionDefaults[section] || {});
    setEditingSection(section);
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setFormData({});
  };

  // ── Save section ──
  const saveSection = async (section) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);

    try {
      let payload = {};

      switch (section) {
        case "social":
          payload = {
            instagram: formData.instagram,
            website: formData.website,
          };
          break;

        case "personal": {
          payload = {
            fullName: formData.fullName,
            gender: formData.gender || undefined,
            nationalityId: formData.nationalityId || undefined,
            children: formData.children !== "" ? formData.children === "true" : undefined,
            civilState: formData.civilState || undefined,
            birthDate: formData.birthDate || undefined,
            whatsapp: formData.whatsapp,
            workEquipments: formData.workEquipments || [],
          };
          const addr = formData.address;
          const hasAddr = addr && Object.values(addr).some(v => v);
          if (hasAddr) payload.address = { ...addr };
          break;
        }

        case "preferences":
          payload = {
            minSalary: formData.minSalary !== "" ? Number(formData.minSalary) : null,
            maxSalary: formData.maxSalary !== "" ? Number(formData.maxSalary) : null,
            preferredPositions: (formData.preferredPositions || [])
              .filter(pp => pp.jobPositionId)
              .map(pp => ({ jobPositionId: pp.jobPositionId, seniority: pp.seniority || "PLENO" })),
            contractModelIds: formData.contractModelIds || [],
            availabilityIds: formData.availabilityIds || [],
            jobModelIds: formData.jobModelIds || [],
            preferredRegions: (formData.preferredRegions || []).map(r => ({
              state: r.state,
              city: r.city || '',
              cityCode: r.cityCode || '',
            })),
          };
          break;

        case "purpose":
          payload = {
            purposeQuestions: (formData.questions || [])
              .filter(q => q.id)
              .map(q => ({ id: q.id, response: q.response || "" })),
          };
          break;

        case "knowledge": {
          const predefinedSkills = (formData.skills || []).filter(s => s.id);
          const customSkills = (formData.skills || []).filter(s => !s.id || s.custom);
          const predefinedTools = (formData.tools || []).filter(t => t.id);
          const customTools = (formData.tools || []).filter(t => !t.id || t.custom);

          payload = {
            skillsIds: predefinedSkills.map(s => s.id),
            customSkills: customSkills.map(s => s.name).filter(Boolean),
            jobToolsIds: predefinedTools.map(t => t.id),
            customTools: customTools.map(t => t.name).filter(Boolean),
            languages: (formData.languages || [])
              .filter(l => l.languageId && l.fluencyLevelId)
              .map(l => ({ id: l.id || undefined, languageId: l.languageId, fluencyLevelId: l.fluencyLevelId })),
            iaProficiency: formData.iaProficiency || undefined,
          };
          break;
        }

        case "pitch":
          payload = {
            professionalPitch: formData.professionalPitch,
          };
          break;

        case "history":
          payload = {
            professionalHistories: (formData.entries || [])
              .filter(h => h.companyName && h.jobPositionId && h.startDate)
              .map(h => ({
                id: h.id || undefined,
                companyName: h.companyName,
                jobPositionId: h.jobPositionId,
                startDate: h.startDate,
                endDate: h.endDate || null,
                description: h.description || "",
                currentJob: h.currentJob ?? false,
              })),
          };
          break;
      }

      const updated = await db.profile.update(payload);
      setProfile(updated);
      setEditingSection(null);
      setFormData({});
      toast({ title: "Perfil atualizado com sucesso!" });
    } catch (err) {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  // ── Toggle Talent Hunter ──
  const toggleSmartMatch = async (checked) => {
    try {
      const updated = await db.profile.update({ allowSmartMatch: checked });
      setProfile(updated);
      toast({ title: checked ? "Visibilidade ativada" : "Visibilidade desativada" });
    } catch (err) {
      toast({ title: "Erro ao atualizar", description: err.message, variant: "destructive" });
    }
  };

  // ── Avatar upload ──
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 2000,
        initialQuality: 0.9,
        useWebWorker: true,
      });
      const { avatarUrl } = await db.profile.uploadAvatar(compressed);
      setProfile(prev => ({ ...prev, avatarUrl }));
      window.dispatchEvent(new CustomEvent("avatar-updated", { detail: { avatarUrl } }));
      toast({ title: "Foto atualizada com sucesso!" });
    } catch (err) {
      toast({ title: "Erro ao enviar foto", description: err.message, variant: "destructive" });
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#00DCA4" }} />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-muted-foreground">Não foi possível carregar o perfil.</p>
      </div>
    );
  }

  // ── Detect incomplete profile (new accounts) ──
  const isProfileIncomplete = !profile.addresses || !profile.gender ||
    (profile.knowledge?.skills?.length === 0 && profile.knowledge?.customSkills?.length === 0);

  if (isProfileIncomplete) {
    return (
      <ProfileOnboarding
        profile={profile}
        onComplete={(updatedProfile) => {
          setProfile(updatedProfile);
        }}
      />
    );
  }

  const lookups = profile.lookups || {};
  const addr = profile.addresses;
  const knowledge = profile.knowledge || {};
  const location = [addr?.city, addr?.state].filter(Boolean).join(" - ");

  // ══════════════════════════════════════════════════════
  // ══  RENDER  ══════════════════════════════════════════
  // ══════════════════════════════════════════════════════

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex flex-col lg:flex-row gap-8">

        {/* ═══════════ SIDEBAR ═══════════ */}
        <aside className="w-full lg:w-80 shrink-0">
          <div className="lg:sticky lg:top-6 space-y-4">
            {/* Profile Card */}
            <Card className="bg-white">
              <CardContent className="pt-6 flex flex-col items-center text-center">
                <div className="relative group mb-3">
                  <Avatar className="w-20 h-20">
                    {profile.avatarUrl && (
                      <AvatarImage src={profile.avatarUrl} alt={profile.fullName} />
                    )}
                    <AvatarFallback
                      className="text-2xl font-bold"
                      style={{ background: "#B0FCEA", color: "#00664C" }}
                    >
                      {getInitials(profile.fullName)}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    className="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                  >
                    {uploadingAvatar ? (
                      <Loader2 className="w-5 h-5 animate-spin text-white" />
                    ) : (
                      <Pencil className="w-4 h-4 text-white" />
                    )}
                  </button>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                </div>
                <h2 className="text-xl font-semibold" style={{ color: "#2E3233" }}>
                  {profile.fullName || "Sem nome"}
                </h2>
                {location && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {location}
                  </p>
                )}

                <Separator className="my-4" />

                {/* Contact links */}
                <div className="flex gap-3">
                  {profile.email && (
                    <a
                      href={`mailto:${profile.email}`}
                      className="p-2 rounded-full hover:bg-muted transition-colors"
                      title={profile.email}
                    >
                      <Mail className="w-4 h-4 text-muted-foreground" />
                    </a>
                  )}
                  {profile.instagram && (
                    <a
                      href={`https://instagram.com/${profile.instagram.replace(/^@/, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full hover:bg-muted transition-colors"
                      title={profile.instagram}
                    >
                      <Instagram className="w-4 h-4 text-muted-foreground" />
                    </a>
                  )}
                  {profile.whatsapp && (
                    <a
                      href={`https://wa.me/${profile.whatsapp.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full hover:bg-muted transition-colors"
                      title={profile.whatsapp}
                    >
                      <Phone className="w-4 h-4 text-muted-foreground" />
                    </a>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Talent Hunter Toggle */}
            <Card className="bg-white">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Visibilidade no Talent Hunter</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Permitir que recrutadores encontrem seu perfil
                    </p>
                  </div>
                  <Switch
                    checked={!!profile.allowSmartMatch}
                    onCheckedChange={toggleSmartMatch}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </aside>

        {/* ═══════════ MAIN CONTENT ═══════════ */}
        <main className="flex-1 space-y-6 min-w-0">

          {/* ── Seção 1: Redes e Portfólio ── */}
          <Card className="bg-white">
            <SectionHeader
              icon={Globe}
              title="Redes e Portfólio"
              section="social"
              editingSection={editingSection}
              onEdit={startEdit}
              onCancel={cancelEdit}
              onSave={saveSection}
              saving={saving}
            />
            <CardContent>
              {editingSection === "social" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Instagram</Label>
                    <Input
                      placeholder="@usuario"
                      value={formData.instagram || ""}
                      onChange={e => updateForm("instagram", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Website / Portfólio</Label>
                    <Input
                      placeholder="https://..."
                      value={formData.website || ""}
                      onChange={e => updateForm("website", e.target.value)}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Instagram</p>
                    <p className="text-sm">{displayValue(profile.instagram)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Website / Portfólio</p>
                    <p className="text-sm">{displayValue(profile.website)}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Seção 2: Dados Pessoais ── */}
          <Card className="bg-white">
            <SectionHeader
              icon={User}
              title="Dados Pessoais"
              section="personal"
              editingSection={editingSection}
              onEdit={startEdit}
              onCancel={cancelEdit}
              onSave={saveSection}
              saving={saving}
            />
            <CardContent>
              {editingSection === "personal" ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nome completo *</Label>
                      <Input
                        value={formData.fullName || ""}
                        onChange={e => updateForm("fullName", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>E-mail</Label>
                      <Input value={profile.email || ""} disabled className="bg-muted" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>CPF</Label>
                      <Input value={profile.cpf || ""} disabled className="bg-muted" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Gênero</Label>
                      <Select value={formData.gender || ""} onValueChange={v => updateForm("gender", v)}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          {GENDER_OPTIONS.map(g => (
                            <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Nacionalidade</Label>
                      <Select value={formData.nationalityId || ""} onValueChange={v => updateForm("nationalityId", v)}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          {(lookups.nationalities || []).map(n => (
                            <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Filhos</Label>
                      <Select value={formData.children || ""} onValueChange={v => updateForm("children", v)}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          {CHILDREN_OPTIONS.map(c => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Estado civil</Label>
                      <Select value={formData.civilState || ""} onValueChange={v => updateForm("civilState", v)}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          {CIVIL_STATE_OPTIONS.map(cs => (
                            <SelectItem key={cs.value} value={cs.value}>{cs.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Data de nascimento</Label>
                      <Input
                        type="date"
                        value={formData.birthDate || ""}
                        onChange={e => updateForm("birthDate", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>WhatsApp</Label>
                      <Input
                        placeholder="+5511999999999"
                        value={formData.whatsapp || ""}
                        onChange={e => updateForm("whatsapp", e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Work Equipments (tags) */}
                  <div className="space-y-2">
                    <Label>Equipamentos de trabalho</Label>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(formData.workEquipments || []).map((eq, i) => (
                        <Badge key={i} variant="secondary" className="gap-1">
                          {eq}
                          <X
                            className="w-3 h-3 cursor-pointer hover:text-destructive"
                            onClick={() => updateForm("workEquipments", formData.workEquipments.filter((_, j) => j !== i))}
                          />
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Ex: Notebook, Câmera..."
                        value={formData.workEquipmentInput || ""}
                        onChange={e => updateForm("workEquipmentInput", e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const val = formData.workEquipmentInput?.trim();
                            if (val) {
                              updateForm("workEquipments", [...(formData.workEquipments || []), val]);
                              updateForm("workEquipmentInput", "");
                            }
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const val = formData.workEquipmentInput?.trim();
                          if (val) {
                            updateForm("workEquipments", [...(formData.workEquipments || []), val]);
                            updateForm("workEquipmentInput", "");
                          }
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Address */}
                  <Separator />
                  <p className="text-sm font-medium text-muted-foreground">Endereço</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>CEP</Label>
                      <Input
                        value={formData.address?.zipCode || ""}
                        onChange={e => updateFormNested("address", "zipCode", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Rua</Label>
                      <Input
                        value={formData.address?.street || ""}
                        onChange={e => updateFormNested("address", "street", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Número</Label>
                      <Input
                        value={formData.address?.streetNumber || ""}
                        onChange={e => updateFormNested("address", "streetNumber", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Complemento</Label>
                      <Input
                        value={formData.address?.complement || ""}
                        onChange={e => updateFormNested("address", "complement", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Bairro</Label>
                      <Input
                        value={formData.address?.neighborhood || ""}
                        onChange={e => updateFormNested("address", "neighborhood", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Cidade</Label>
                      <Input
                        value={formData.address?.city || ""}
                        onChange={e => updateFormNested("address", "city", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Estado</Label>
                      <Input
                        value={formData.address?.state || ""}
                        onChange={e => updateFormNested("address", "state", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Nome completo</p>
                      <p className="text-sm">{displayValue(profile.fullName)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">E-mail</p>
                      <p className="text-sm">{displayValue(profile.email)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">CPF</p>
                      <p className="text-sm">{displayValue(profile.cpf)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Gênero</p>
                      <p className="text-sm">{labelFromValue(profile.gender, GENDER_OPTIONS)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Nacionalidade</p>
                      <p className="text-sm">{lookupName(profile.nationalityId, lookups.nationalities)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Filhos</p>
                      <p className="text-sm">
                        {profile.children != null ? labelFromValue(String(profile.children), CHILDREN_OPTIONS) : "—"}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Estado civil</p>
                      <p className="text-sm">{labelFromValue(profile.civilState, CIVIL_STATE_OPTIONS)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Data de nascimento</p>
                      <p className="text-sm">
                        {profile.birthDate
                          ? new Date(profile.birthDate).toLocaleDateString("pt-BR")
                          : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">WhatsApp</p>
                      <p className="text-sm">{displayValue(profile.whatsapp)}</p>
                    </div>
                  </div>
                  {(profile.workEquipments || []).length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Equipamentos de trabalho</p>
                      <div className="flex flex-wrap gap-1">
                        {profile.workEquipments.map((eq, i) => (
                          <Badge key={i} variant="secondary">{eq}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {addr && (
                    <div>
                      <p className="text-xs text-muted-foreground">Endereço</p>
                      <p className="text-sm">
                        {[
                          addr.street && `${addr.street}${addr.streetNumber ? `, ${addr.streetNumber}` : ""}`,
                          addr.complement,
                          addr.neighborhood,
                          addr.city && addr.state ? `${addr.city} - ${addr.state}` : addr.city || addr.state,
                          addr.zipCode && `CEP: ${addr.zipCode}`,
                        ].filter(Boolean).join(" · ") || "—"}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Seção 3: Preferências ── */}
          <Card className="bg-white">
            <SectionHeader
              icon={Heart}
              title="Preferências"
              section="preferences"
              editingSection={editingSection}
              onEdit={startEdit}
              onCancel={cancelEdit}
              onSave={saveSection}
              saving={saving}
            />
            <CardContent>
              {editingSection === "preferences" ? (
                <div className="space-y-4">
                  {/* Cargos de interesse */}
                  <div className="space-y-2">
                    <Label>Cargos de interesse</Label>
                    {(formData.preferredPositions || []).map((pp, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <Select
                          value={pp.jobPositionId || ""}
                          onValueChange={v => {
                            const updated = [...formData.preferredPositions];
                            updated[i] = { ...updated[i], jobPositionId: v };
                            updateForm("preferredPositions", updated);
                          }}
                        >
                          <SelectTrigger className="flex-1"><SelectValue placeholder="Cargo" /></SelectTrigger>
                          <SelectContent>
                            {(lookups.positions || []).map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={pp.seniority || "PLENO"}
                          onValueChange={v => {
                            const updated = [...formData.preferredPositions];
                            updated[i] = { ...updated[i], seniority: v };
                            updateForm("preferredPositions", updated);
                          }}
                        >
                          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SENIORITY_OPTIONS.map(s => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => updateForm("preferredPositions", formData.preferredPositions.filter((_, j) => j !== i))}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline" size="sm"
                      onClick={() => updateForm("preferredPositions", [...(formData.preferredPositions || []), { jobPositionId: "", seniority: "PLENO" }])}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Adicionar cargo
                    </Button>
                  </div>

                  {/* Faixa salarial */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Salário mínimo (R$)</Label>
                      <Input
                        type="number"
                        placeholder="5000"
                        value={formData.minSalary ?? ""}
                        onChange={e => updateForm("minSalary", e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Salário máximo (R$)</Label>
                      <Input
                        type="number"
                        placeholder="10000"
                        value={formData.maxSalary ?? ""}
                        onChange={e => updateForm("maxSalary", e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Formato de contratação */}
                  <div className="space-y-2">
                    <Label>Formato de contratação</Label>
                    <div className="flex flex-wrap gap-2">
                      {(lookups.contractModels || []).map(cm => {
                        const selected = (formData.contractModelIds || []).includes(cm.id);
                        return (
                          <Badge
                            key={cm.id}
                            variant={selected ? "default" : "outline"}
                            className="cursor-pointer"
                            onClick={() => {
                              const ids = formData.contractModelIds || [];
                              updateForm("contractModelIds", selected ? ids.filter(id => id !== cm.id) : [...ids, cm.id]);
                            }}
                          >
                            {cm.name}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Disponibilidade */}
                    <div className="space-y-2">
                      <Label>Disponibilidade</Label>
                      <div className="flex flex-wrap gap-2">
                        {(lookups.availability || []).map(av => {
                          const selected = (formData.availabilityIds || []).includes(av.id);
                          return (
                            <Badge
                              key={av.id}
                              variant={selected ? "default" : "outline"}
                              className="cursor-pointer"
                              onClick={() => {
                                const ids = formData.availabilityIds || [];
                                updateForm("availabilityIds", selected ? ids.filter(id => id !== av.id) : [...ids, av.id]);
                              }}
                            >
                              {av.name}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>

                    {/* Formato de trabalho */}
                    <div className="space-y-2">
                      <Label>Formato de trabalho</Label>
                      <div className="flex flex-wrap gap-2">
                        {(lookups.jobModels || []).map(jm => {
                          const selected = (formData.jobModelIds || []).includes(jm.id);
                          return (
                            <Badge
                              key={jm.id}
                              variant={selected ? "default" : "outline"}
                              className="cursor-pointer"
                              onClick={() => {
                                const ids = formData.jobModelIds || [];
                                const newIds = selected ? ids.filter(id => id !== jm.id) : [...ids, jm.id];
                                updateForm("jobModelIds", newIds);
                                // Clear regions if no HYBRID/IN_PERSON models remain
                                const hasPresential = (lookups.jobModels || []).some(
                                  m => newIds.includes(m.id) && ['HYBRID', 'IN_PERSON'].includes(m.workModality)
                                );
                                if (!hasPresential) {
                                  updateForm("preferredRegions", []);
                                }
                              }}
                            >
                              {jm.name}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Regiões de interesse (condicional) */}
                  {(() => {
                    const selectedIds = formData.jobModelIds || [];
                    const showRegions = (lookups.jobModels || []).some(
                      jm => selectedIds.includes(jm.id) && ['HYBRID', 'IN_PERSON'].includes(jm.workModality)
                    );
                    if (!showRegions) return null;
                    return (
                      <div className="space-y-3 mt-4 pt-4 border-t">
                        <Label>Regiões de interesse</Label>
                        <RegionAdder
                          onAdd={(region) => updateForm("preferredRegions", [...(formData.preferredRegions || []), region])}
                        />
                        {(formData.preferredRegions || []).length > 0 && (
                          <div className="space-y-2 mt-2">
                            {formData.preferredRegions.map((r, i) => (
                              <div key={i} className="flex items-center justify-between rounded-md border px-3 py-2">
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium">
                                    {r.city ? formatCityName(r.city) : "Todas as cidades"}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {STATES.find(s => s.value === r.state)?.label || r.state}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  className="text-muted-foreground hover:text-destructive"
                                  onClick={() => updateForm("preferredRegions", formData.preferredRegions.filter((_, j) => j !== i))}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {(formData.preferredRegions || []).length === 0 && (
                          <p className="text-xs text-amber-600">Selecione pelo menos uma região de interesse.</p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Cargos de interesse (view) */}
                  {(profile.preferredPositions || []).length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground">Cargos de interesse</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {profile.preferredPositions.map((pp, i) => (
                          <Badge key={i} variant="secondary">
                            {pp.jobPosition} {pp.seniority ? labelFromValue(pp.seniority, SENIORITY_OPTIONS) : ""}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Faixa salarial</p>
                      <p className="text-sm">
                        {profile.minSalary != null || profile.maxSalary != null
                          ? `R$ ${Number(profile.minSalary || 0).toLocaleString("pt-BR")} - R$ ${Number(profile.maxSalary || 0).toLocaleString("pt-BR")}`
                          : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Formato de contratação</p>
                      <p className="text-sm">
                        {(profile.contractModels || []).length > 0
                          ? profile.contractModels.map(c => c.name).join("; ")
                          : "—"}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Disponibilidade</p>
                      <p className="text-sm">
                        {(profile.availability || []).length > 0
                          ? profile.availability.map(a => a.name).join("; ")
                          : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Formato de trabalho</p>
                      <p className="text-sm">
                        {(profile.jobModels || []).length > 0
                          ? profile.jobModels.map(j => j.name).join("; ")
                          : "—"}
                      </p>
                    </div>
                  </div>

                  {/* Regiões de interesse (view) */}
                  {(profile.preferredRegions || []).length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground">Regiões de interesse</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {profile.preferredRegions.map((r, i) => (
                          <Badge key={i} variant="secondary">
                            {r.city ? `${formatCityName(r.city)}, ${r.state}` : STATES.find(s => s.value === r.state)?.label || r.state}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Seção 4: Conhecimentos ── */}
          <Card className="bg-white">
            <SectionHeader
              icon={Brain}
              title="Conhecimentos"
              section="knowledge"
              editingSection={editingSection}
              onEdit={startEdit}
              onCancel={cancelEdit}
              onSave={saveSection}
              saving={saving}
            />
            <CardContent>
              {editingSection === "knowledge" ? (
                <div className="space-y-6">
                  {/* Languages */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Idiomas</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          updateForm("languages", [
                            ...(formData.languages || []),
                            { languageId: "", language: "", fluencyLevelId: "", fluencyLevel: "" },
                          ])
                        }
                      >
                        <Plus className="w-4 h-4 mr-1" /> Adicionar
                      </Button>
                    </div>
                    {(formData.languages || []).length === 0 && (
                      <p className="text-sm text-muted-foreground">Nenhum idioma adicionado.</p>
                    )}
                    {(formData.languages || []).map((lang, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Select
                          value={lang.languageId || ""}
                          onValueChange={v => {
                            const opt = lookups.languages?.find(l => l.id === v);
                            const updated = [...formData.languages];
                            updated[i] = { ...updated[i], languageId: v, language: opt?.name || "" };
                            updateForm("languages", updated);
                          }}
                        >
                          <SelectTrigger className="flex-1">
                            <SelectValue placeholder="Idioma">{lang.language || "Idioma"}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {(lookups.languages || []).map(l => (
                              <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={lang.fluencyLevelId || ""}
                          onValueChange={v => {
                            const opt = lookups.fluencyLevels?.find(f => f.id === v);
                            const updated = [...formData.languages];
                            updated[i] = { ...updated[i], fluencyLevelId: v, fluencyLevel: opt?.name || "" };
                            updateForm("languages", updated);
                          }}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Fluência">{lang.fluencyLevel || "Fluência"}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {(lookups.fluencyLevels || []).map(f => (
                              <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-destructive shrink-0"
                          onClick={() =>
                            updateForm("languages", formData.languages.filter((_, j) => j !== i))
                          }
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  {/* Skills multi-select */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Skills</Label>
                    <MultiSelectSearchable
                      options={lookups.skills || []}
                      selected={formData.skills || []}
                      onChange={val => updateForm("skills", val)}
                      placeholder="Buscar ou criar skills..."
                      allowCustom
                    />
                  </div>

                  <Separator />

                  {/* Tools multi-select */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Ferramentas</Label>
                    <MultiSelectSearchable
                      options={lookups.tools || []}
                      selected={formData.tools || []}
                      onChange={val => updateForm("tools", val)}
                      placeholder="Buscar ou criar ferramentas..."
                      allowCustom
                    />
                  </div>

                  <Separator />

                  {/* IA Proficiency */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Proficiência em IA</Label>
                    <Select
                      value={formData.iaProficiency || ""}
                      onValueChange={v => updateForm("iaProficiency", v)}
                    >
                      <SelectTrigger className="w-full sm:w-64">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {IA_PROFICIENCY_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Languages view */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Idiomas</p>
                    {(knowledge.languages || []).length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {knowledge.languages.map((l, i) => (
                          <Badge key={i} variant="outline">
                            {l.language} · {l.fluencyLevel}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">—</p>
                    )}
                  </div>

                  {/* Skills view */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Skills</p>
                    {((knowledge.skills || []).length > 0 || (knowledge.customSkills || []).length > 0) ? (
                      <div className="flex flex-wrap gap-1">
                        {(knowledge.skills || []).map(s => (
                          <Badge key={s.id} variant="secondary">{s.name}</Badge>
                        ))}
                        {(knowledge.customSkills || []).map((s, i) => (
                          <Badge key={`cs-${i}`} variant="secondary">{s.name || s}</Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">—</p>
                    )}
                  </div>

                  {/* Tools view */}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Ferramentas</p>
                    {((knowledge.tools || []).length > 0 || (knowledge.customTools || []).length > 0) ? (
                      <div className="flex flex-wrap gap-1">
                        {(knowledge.tools || []).map(t => (
                          <Badge key={t.id} variant="secondary">{t.name}</Badge>
                        ))}
                        {(knowledge.customTools || []).map((t, i) => (
                          <Badge key={`ct-${i}`} variant="secondary">{t.name || t}</Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">—</p>
                    )}
                  </div>

                  {/* IA Proficiency view */}
                  <div>
                    <p className="text-xs text-muted-foreground">Proficiência em IA</p>
                    <p className="text-sm">
                      {labelFromValue(knowledge.iaProficiency || profile.iaProficiency, IA_PROFICIENCY_OPTIONS)}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Seção 5: Pitch Profissional ── */}
          <Card className="bg-white">
            <SectionHeader
              icon={FileText}
              title="Pitch Profissional"
              section="pitch"
              editingSection={editingSection}
              onEdit={startEdit}
              onCancel={cancelEdit}
              onSave={saveSection}
              saving={saving}
            />
            <CardContent>
              {editingSection === "pitch" ? (
                <div className="space-y-2">
                  <RichTextEditor
                    value={formData.professionalPitch || ""}
                    onChange={val => updateForm("professionalPitch", val)}
                    placeholder="Descreva brevemente seu perfil profissional..."
                  />
                </div>
              ) : (
                <div>
                  {profile.professionalPitch ? (
                    <RichTextViewer content={profile.professionalPitch} />
                  ) : (
                    <p className="text-sm text-muted-foreground">Nenhum pitch profissional cadastrado.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Seção 6: Trajetória Profissional ── */}
          <Card className="bg-white">
            <SectionHeader
              icon={Briefcase}
              title="Trajetória Profissional"
              section="history"
              editingSection={editingSection}
              onEdit={startEdit}
              onCancel={cancelEdit}
              onSave={saveSection}
              saving={saving}
            />
            <CardContent>
              {editingSection === "history" ? (
                <div className="space-y-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      updateForm("entries", [
                        ...(formData.entries || []),
                        {
                          companyName: "",
                          jobPositionId: "",
                          jobPosition: "",
                          startDate: "",
                          endDate: "",
                          description: "",
                          currentJob: false,
                        },
                      ])
                    }
                  >
                    <Plus className="w-4 h-4 mr-1" /> Adicionar experiência
                  </Button>

                  {(formData.entries || []).length === 0 && (
                    <p className="text-sm text-muted-foreground">Nenhuma experiência adicionada.</p>
                  )}

                  {(formData.entries || []).map((entry, i) => (
                    <div key={i} className="relative border rounded-lg p-4 space-y-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"
                        onClick={() =>
                          updateForm("entries", formData.entries.filter((_, j) => j !== i))
                        }
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Empresa</Label>
                          <Input
                            value={entry.companyName || ""}
                            onChange={e => {
                              const updated = [...formData.entries];
                              updated[i] = { ...updated[i], companyName: e.target.value };
                              updateForm("entries", updated);
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Cargo</Label>
                          <Select
                            value={entry.jobPositionId || ""}
                            onValueChange={v => {
                              const opt = lookups.positions?.find(p => p.id === v);
                              const updated = [...formData.entries];
                              updated[i] = { ...updated[i], jobPositionId: v, jobPosition: opt?.name || "" };
                              updateForm("entries", updated);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o cargo">
                                {entry.jobPosition || "Selecione o cargo"}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {(lookups.positions || []).map(p => (
                                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                        <div className="space-y-2">
                          <Label>Data de início</Label>
                          <Input
                            type="date"
                            value={entry.startDate || ""}
                            onChange={e => {
                              const updated = [...formData.entries];
                              updated[i] = { ...updated[i], startDate: e.target.value };
                              updateForm("entries", updated);
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Data de término</Label>
                          <Input
                            type="date"
                            disabled={entry.currentJob}
                            value={entry.endDate || ""}
                            onChange={e => {
                              const updated = [...formData.entries];
                              updated[i] = { ...updated[i], endDate: e.target.value || null };
                              updateForm("entries", updated);
                            }}
                          />
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer pb-1">
                          <input
                            type="checkbox"
                            checked={!!entry.currentJob}
                            onChange={e => {
                              const updated = [...formData.entries];
                              updated[i] = {
                                ...updated[i],
                                currentJob: e.target.checked,
                                endDate: e.target.checked ? "" : updated[i].endDate,
                              };
                              updateForm("entries", updated);
                            }}
                            className="w-4 h-4 rounded border-gray-300"
                            style={{ accentColor: "#00DCA4" }}
                          />
                          <span className="text-sm">Trabalho atual</span>
                        </label>
                      </div>

                      <div className="space-y-2">
                        <Label>Descrição</Label>
                        <RichTextEditor
                          value={entry.description || ""}
                          onChange={val => {
                            const updated = [...formData.entries];
                            updated[i] = { ...updated[i], description: val };
                            updateForm("entries", updated);
                          }}
                          placeholder="Descreva suas responsabilidades e realizações..."
                          maxLength={250}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  {(profile.professionalHistory || []).length > 0 ? (
                    <div className="space-y-4">
                      {profile.professionalHistory.map((entry, i) => (
                        <div key={i} className="relative pl-6 pb-4 last:pb-0">
                          {/* Timeline line */}
                          {i < profile.professionalHistory.length - 1 && (
                            <div
                              className="absolute left-[9px] top-3 bottom-0 w-px bg-border"
                            />
                          )}
                          {/* Timeline dot */}
                          <div
                            className="absolute left-0 top-1.5 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center"
                            style={{
                              borderColor: entry.currentJob ? "#00DCA4" : "#d4d4d8",
                              background: entry.currentJob ? "#B0FCEA" : "white",
                            }}
                          >
                            {entry.currentJob && (
                              <div className="w-2 h-2 rounded-full" style={{ background: "#00DCA4" }} />
                            )}
                          </div>

                          <div>
                            <p className="text-sm font-medium">{entry.jobPosition}</p>
                            <p className="text-sm text-muted-foreground">{entry.companyName}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {entry.startDate
                                ? new Date(entry.startDate).toLocaleDateString("pt-BR", { month: "short", year: "numeric" })
                                : ""}
                              {" — "}
                              {entry.currentJob
                                ? "Presente"
                                : entry.endDate
                                  ? new Date(entry.endDate).toLocaleDateString("pt-BR", { month: "short", year: "numeric" })
                                  : ""}
                            </p>
                            {entry.description && (
                              <div className="mt-1">
                                <RichTextViewer content={entry.description} />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Nenhuma experiência profissional cadastrada.</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Seção 7: Vida e Propósito ── */}
            <Card className="bg-white">
              <SectionHeader
                icon={HeartHandshake}
                title="Vida e Propósito"
                section="purpose"
                editingSection={editingSection}
                onEdit={startEdit}
                onCancel={cancelEdit}
                onSave={saveSection}
                saving={saving}
              />
              <CardContent>
                {editingSection === "purpose" ? (
                  <div className="space-y-6">
                    {PURPOSE_QUESTIONS_ORDER.map(qKey => {
                      const q = (formData.questions || []).find(q => q.purposeQuestion === qKey);
                      return (
                        <div key={qKey} className="space-y-2">
                          <Label className="text-sm font-medium">{PURPOSE_QUESTION_LABELS[qKey]}</Label>
                          <RichTextEditor
                            value={q?.response || ""}
                            onChange={val => {
                              const existing = formData.questions || [];
                              const hasKey = existing.some(item => item.purposeQuestion === qKey);
                              const updated = hasKey
                                ? existing.map(item =>
                                    item.purposeQuestion === qKey ? { ...item, response: val } : item
                                  )
                                : [...existing, { purposeQuestion: qKey, response: val }];
                              updateForm("questions", updated);
                            }}
                            placeholder="Escreva sua resposta..."
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {PURPOSE_QUESTIONS_ORDER.map((qKey, i) => {
                      const q = (profile.purposeQuestions || []).find(q => q.purposeQuestion === qKey);
                      return (
                        <div key={qKey}>
                          {i > 0 && <Separator className="mb-4" />}
                          <p className="text-xs text-muted-foreground mb-1">{PURPOSE_QUESTION_LABELS[qKey]}</p>
                          {q?.response ? (
                            <RichTextViewer content={q.response} />
                          ) : (
                            <p className="text-sm text-muted-foreground">—</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

        </main>
      </div>
    </div>
  );
}
